import { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { toast } from 'react-toastify'
import { toastSyncSuccess } from 'src/utils/syncToast'
import { useNavigate } from 'react-router-dom'
import { useAuth } from 'src/contexts/auth'
import { CreateAnimalForm } from 'src/@types/validations/Animal'
import { useUpdateDiagnosisWithBirth } from 'src/hooks/api/pregnancy-diagnosis'
import { IAnimal } from 'src/dto/animal.dto'
import { SaveDiagnosisWithBirthParams } from 'src/services/api/pregnancy-diagnosis.service'
import { AnimalController } from 'src/controllers/animal.controller'
import { PregnancyDiagnosisController } from 'src/controllers/pregnancy-diagnosis.controller'
import { BirthInfo } from '../../components/DiagnosisModal'
import ROUTES from 'src/constants/routes'
import { calculateGestationTime } from '../../utils/functions'
import { useQueryClient } from '@tanstack/react-query'
import { BirthRegistrationParams, defaultFormValues } from '..'

export const useBirthRegistration = () => {
  const { userFarm: farmId } = useAuth()
  const navigate = useNavigate()

  const [selectedAnimal, setSelectedAnimal] = useState<IAnimal | null>(null)
  const [savedBirths, setSavedBirths] = useState<CreateAnimalForm[]>([])
  const [isDiagnosisModalOpen, setIsDiagnosisModalOpen] = useState(false)
  const [useDateInterval, setUseDateInterval] = useState(false)
  const [isLoadingAnimal, setIsLoadingAnimal] = useState(false)
  const [showBirthWarningModal, setShowBirthWarningModal] = useState(false)

  const { mutateAsync: saveDiagnosisComplete, isPending } = useUpdateDiagnosisWithBirth()
  const queryClient = useQueryClient()

  const { watch, setError, setValue, clearErrors, handleSubmit, reset } = useFormContext<BirthRegistrationParams>()

  const handleSelectAnimal = async (animalId: string) => {
    if (!animalId) {
      setSelectedAnimal(null)
      return
    }

    setIsLoadingAnimal(true)
    try {
      const animalData = await queryClient.fetchQuery({
        queryKey: ['getAnimalById', farmId, animalId],
        queryFn: () => new AnimalController().getAnimalById(farmId!, animalId)
      })

      if (animalData) {
        const pregnancyDiagnosisController = new PregnancyDiagnosisController()
        const diagnosisList = await pregnancyDiagnosisController.getPregnancyDiagnosisListByAnimalId(farmId!, animalId)
        const pendingDiagnosis = diagnosisList.find((diagnosis) => !diagnosis.idCria || diagnosis.idCria.length === 0)

        if (pendingDiagnosis) {
          const url = `${ROUTES.REPRODUCTION.toPregnancyListDetails(
            pendingDiagnosis.idFazenda,
            pendingDiagnosis.idAnimal,
            pendingDiagnosis.idDiagGest
          )}&openDiagnosis=true`

          navigate(url)
          return
        }

        setShowBirthWarningModal(true)
        setSelectedAnimal(animalData)
        setValue('idAnimal', animalId)
        clearErrors('idAnimal')
      }
    } catch (error) {
      toast.error('Erro ao buscar informações do animal')
      console.error(error)
    } finally {
      setIsLoadingAnimal(false)
    }
  }

  const handleConfirmWarningDiagnosisModal = () => {
    setShowBirthWarningModal(false)
  }

  const handleCancelWarningDiagnosisModal = () => {
    setShowBirthWarningModal(false)
    setSelectedAnimal(null)
    setSavedBirths([])
    setUseDateInterval(false)
    reset(defaultFormValues)
  }

  const handleToggleDateInterval = () => {
    const nextUseDateInterval = !useDateInterval
    setUseDateInterval(nextUseDateInterval)

    if (nextUseDateInterval) {
      const beginDate = new Date()
      beginDate.setDate(beginDate.getDate() - 1)
      const endDate = new Date()

      setValue('dataInicial', beginDate.toISOString())
      setValue('dataFim', endDate.toISOString())
    } else {
      setValue('dataCobertura', new Date().toISOString())
    }
  }

  const handleOpenDiagnosisModal = () => {
    const animalId = watch('idAnimal')
    const tipoReproducao = watch('tipoReproducao')
    const touro = watch('touro')

    if (!animalId) {
      setError('touro', { message: 'Vaca é obrigatório' })
      return
    }

    if (!tipoReproducao) {
      setError('tipoReproducao', { message: 'Selecione o tipo de reprodução' })
      return
    }

    if (!touro) {
      setError('touro', { message: 'Touro é obrigatório' })
      return
    }

    setIsDiagnosisModalOpen(true)
  }

  const handleSaveBirths = (births: CreateAnimalForm[], birthInfo: BirthInfo) => {
    setSavedBirths(births)
    setValue('dataCobertura', birthInfo.dataParto)
    setValue('tipoParto', birthInfo.tipoParto)
    setIsDiagnosisModalOpen(false)
  }

  const handleCloseDiagnosisModal = () => {
    setIsDiagnosisModalOpen(false)
  }

  const handleSaveAll = handleSubmit(async (formData) => {
    if (savedBirths.length === 0) {
      return
    }

    try {
      let dataReproducao: Date
      if (useDateInterval) {
        const dataInicio = new Date(formData.dataInicial || '')
        const dataFim = new Date(formData.dataFim || '')
        const beginTime = dataInicio.getTime()
        const endTime = dataFim.getTime()
        const averageTime = beginTime + (endTime - beginTime) / 2
        dataReproducao = new Date(averageTime)
      } else {
        dataReproducao = new Date(formData.dataCobertura || '')
      }

      const gestationTime = calculateGestationTime(formData.dataParto || undefined, dataReproducao.toISOString())

      const diagnosisPayload: SaveDiagnosisWithBirthParams = {
        idFazenda: farmId!,
        idAnimal: formData.idAnimal,
        touro: formData.touro,
        tipoReproducao: formData.tipoReproducao,
        dataInicial: dataReproducao.toISOString(),
        dataCobertura: dataReproducao.toISOString(),
        dataDiagnostico: formData.dataParto,
        diagnostico: 'prenha',
        dataParto: formData.dataParto,
        tipoParto: formData.tipoParto || 'normal',
        tempoGestacao: gestationTime,
        nascimento: savedBirths,
        acao: 'Atualizacao',
        status: 'finalizado'
      }

      await saveDiagnosisComplete(diagnosisPayload)

      toastSyncSuccess('Nascimento cadastrado com sucesso!')

      reset()
      setSavedBirths([])
      setSelectedAnimal(null)
      setUseDateInterval(false)
    } catch (error) {
      console.error('Erro ao salvar nascimento:', error)
      toast.error('Erro ao salvar nascimento')
    }
  })

  const handleRemoveBirth = (index: number) => {
    setSavedBirths((prev) => prev.filter((_, i) => i !== index))
  }

  return {
    // State
    selectedAnimal,
    savedBirths,
    isDiagnosisModalOpen,
    useDateInterval,
    isLoadingAnimal,
    showBirthWarningModal,
    isSaving: isPending,

    // Actions
    handleSelectAnimal,
    handleToggleDateInterval,
    handleOpenDiagnosisModal,
    handleSaveBirths,
    handleCloseDiagnosisModal,
    handleSaveAll,
    handleRemoveBirth,
    setShowBirthWarningModal,
    handleConfirmWarningDiagnosisModal,
    handleCancelWarningDiagnosisModal
  }
}
